// contexts/ReportsDownloadContext.tsx
import * as React from 'react';

import { randomUUID } from 'js/lib/uuid';

import type { REPORT_STEPS } from 'bundles/enterprise-admin-analytics/components/DrawerPollingSteps';
import type { ReportType } from 'bundles/enterprise-admin-analytics/types';

export type ReportDownload = {
  id: string;
  reportType: ReportType;
  reportName?: string;
  fileName: string;
  presignedUrl?: string;
  reportUrl?: string;
  status: REPORT_STEPS;
  progress?: number;
  startTime?: number;
};

type StatusChangeCallback = (newStatus: string) => void;

type ReportsStatusStateContextValue = {
  downloads: ReportDownload[];
};

type ReportsStatusActionsContextValue = {
  addDownload: (download: Omit<ReportDownload, 'id'>) => string;
  updateDownload: (id: string, updates: Partial<ReportDownload>) => void;
  removeDownload: (id: string) => void;
  clearDownloads: () => void;
  registerStatusChangeCallback: (callback: StatusChangeCallback) => () => void;
};

const ReportsStatusStateContext = React.createContext<ReportsStatusStateContextValue | undefined>(undefined);
const ReportsStatusActionsContext = React.createContext<ReportsStatusActionsContextValue | undefined>(undefined);

export const ReportsStatusProvider = ({ children }: { children: React.ReactNode }) => {
  const [downloads, setDownloads] = React.useState<ReportDownload[]>([]);
  const statusChangeCallbacksRef = React.useRef<Set<StatusChangeCallback>>(new Set());

  const registerStatusChangeCallback = React.useCallback<(callback: StatusChangeCallback) => () => void>(
    (callback: StatusChangeCallback) => {
      statusChangeCallbacksRef.current.add(callback);

      return () => {
        statusChangeCallbacksRef.current.delete(callback);
      };
    },
    []
  );

  const updateDownload = React.useCallback<(id: string, updates: Partial<ReportDownload>) => void>(
    (id: string, updates: Partial<ReportDownload>) => {
      setDownloads((prev) =>
        prev.map((d) => {
          if (d.id === id) {
            const updated = { ...d, ...updates };
            // Call all registered callbacks if status changed
            if (updates.status && d.status !== updates.status) {
              const newStatus = updates.status;
              statusChangeCallbacksRef.current.forEach((callback) => {
                callback(newStatus);
              });
            }
            return updated;
          }
          return d;
        })
      );
    },
    []
  );

  const removeDownload = React.useCallback<(id: string) => void>((id: string) => {
    setDownloads((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const addDownload = React.useCallback((download: Omit<ReportDownload, 'id'>) => {
    const id = `${download.reportType}-${randomUUID()}`;
    const newDownload: ReportDownload = {
      ...download,
      id,
      startTime: Date.now(),
      progress: 0,
    };

    setDownloads((prev) => [...prev, newDownload]);
    return id;
  }, []);

  const clearDownloads = React.useCallback<() => void>(() => {
    setDownloads([]);
  }, []);

  // Memoize state separately from actions to prevent unnecessary re-renders
  const stateValue = React.useMemo<ReportsStatusStateContextValue>(() => ({ downloads }), [downloads]);

  const actionsValue = React.useMemo<ReportsStatusActionsContextValue>(
    () => ({
      addDownload,
      updateDownload,
      removeDownload,
      clearDownloads,
      registerStatusChangeCallback,
    }),
    [addDownload, updateDownload, removeDownload, clearDownloads, registerStatusChangeCallback]
  );

  return (
    <ReportsStatusActionsContext.Provider value={actionsValue}>
      <ReportsStatusStateContext.Provider value={stateValue}>{children}</ReportsStatusStateContext.Provider>
    </ReportsStatusActionsContext.Provider>
  );
};

// Hook to access only the state (downloads) - use this when you need downloads array
export const useReportsStatusState = () => {
  const context = React.useContext(ReportsStatusStateContext);
  if (!context) {
    throw new Error('useReportsStatusState must be used within ReportsStatusProvider');
  }
  return context;
};

// Hook to access only the actions - use this when you only need functions (prevents re-renders)
export const useReportsStatusActions = () => {
  const context = React.useContext(ReportsStatusActionsContext);
  if (!context) {
    throw new Error('useReportsStatusActions must be used within ReportsStatusProvider');
  }
  return context;
};
